/* omn-tub-buybox.js — behaviour for snippets/omn-tub-buybox.liquid (Uniq Creative, Oct 2026).
 *
 * Simplified tub buy box: colour → overflow drilling → tap drilling → waste → insulation → notes → add.
 * Everything is added in ONE /cart/add.js call as separate lines sharing an _omn_build id.
 * Service lines carry the same properties the Drawing Room wrote, so the checkout sign-off
 * Function (_omn_service), the drilling-evidence webhook (_omn_drill_evidence) and the
 * packing slip keep working unchanged. Customer notes go to the cart attribute
 * "Customer notes" (internal only — never the order note, which is an audit log).
 * Insulation is priced, sized and turned into cart lines by window.OmnInsul (assets/omn-insulation.js),
 * the same module the Drawing Room used: one line on the "Omnitub Insulation - Thermal Build" variant
 * for that £25 step (so no property can move money) and the spec stamped on the tub line too.
 * Matt finish (29 Oct 2026): the Drawing Room's rule — +25% of the colour-inclusive tub price,
 * rounded UP to the next £5, +5 working days, non-returnable. The tub line carries "Finish: Matt"
 * (+ _omn_matte_price for reference); the panel-price Cart Transform recomputes and adds the fee
 * server-side, so this page only displays it (mattFee() mirrors the Function's maths exactly).
 */
(function () {
  'use strict';

  /* Overflow / tap availability per range. Mirrors the signed-off OVERFLOW / TAPS tables in
     omnitub-drilling-evidence src/index.js, which re-validates every spec server-side. */
  var RULES = {
    deep600: { ov: { A: 'flush', B: 'flush', C: 'flush', D: 'flush' }, taps: true,  soak: 525 },
    sheer:   { ov: { A: 'flush', B: 'proud', C: null,    D: 'proud' }, taps: true,  soak: 525, cNote: 'recline end' },
    seated:  { ov: { A: 'proud', B: 'proud', C: null,    D: 'proud' }, taps: false, soak: 925, cNote: 'seat end' }
  };
  var SIDE_POS = { A: [8, 50], B: [50, 8], C: [92, 50], D: [50, 92] };
  var FIN_HEX = {
    'brushed brass': '#B89B5E', 'brushed black': '#3a3a3a', 'brushed bronze': '#7d5a3c',
    'matt black': '#1c1c1c', 'matt white': '#f1efe9', 'polished black': '#0a0a0a',
    'polished white': '#ffffff', 'gloss black': '#0a0a0a', 'gloss white': '#ffffff',
    'chrome': '#cdd1d5', 'stainless steel': '#b9bdc1'
  };

  function money(cents) {
    var n = (Number(cents) || 0) / 100;
    var s = n.toFixed(2);   // always pennies, matching the storefront's money format
    var parts = s.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '£' + parts.join('.');
  }
  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  /* 25% rounded to the penny, then UP to the next £5 — panel-price run.rs matte_component_minor() */
  function mattFee(pence) { pence = Number(pence) || 0; return pence > 0 ? Math.ceil(Math.round(pence * 0.25) / 500) * 500 : 0; }
  function slugify(code) { return String(code).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
  function uid() {
    try { if (window.crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }
  function track(name, data) {
    try {
      var p = Object.assign({ event: name }, data || {});
      if (window.dataLayer && window.dataLayer.push) window.dataLayer.push(p);
      if (typeof window.omnEvent === 'function') window.omnEvent(name, p);
    } catch (e) {}
  }

  function init(root) {
    if (root.__otb) return;
    root.__otb = true;
    var cfgEl = root.querySelector('[data-otb-config]');
    var C;
    try { C = JSON.parse(cfgEl.textContent); } catch (e) { return; }
    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    var R = RULES[C.range] || null;
    var WASTES = {};
    (C.wastes || []).forEach(function (w) { WASTES[w.handle] = w; });

    var S = {
      colour: 'white', ral: null, ref: '',
      ov: false, side: null,
      taps: false,
      insul: false, ins: { base: 50, walls: 50, seat: 50 },
      matt: false,
      waste: '', finish: null,
      notes: ($('[data-otb-notes]') || {}).value || '',
      notesDirty: false
    };
    var checked = root.querySelector('input[name$="-colour"]:checked');
    if (checked) S.colour = checked.value;

    /* ---------- colour ---------- */
    function tubVariant() {
      if (S.colour === 'white' && C.white) return C.white;
      return C.custom || C.white;
    }
    /* Hidden mirror form (.js-product-form-main) so the delivery estimate and other page scripts
       that read the main product form see the variant actually being bought. */
    function mirror(v) {
      var i = $('[data-otb-mirror-id]');
      if (i && v && String(i.value) !== String(v.id)) i.value = v.id;
    }
    var lastVariantId = tubVariant() && tubVariant().id;

    function setColour(val, silent) {
      S.colour = val;
      var r = root.querySelector('input[name$="-colour"][value="' + val + '"]');
      if (r && !r.checked) r.checked = true;
      var ral = $('[data-otb-ral]');
      if (ral) ral.hidden = (val !== 'custom');
      if (val === 'custom') loadColours();
      clearError();
      var v = tubVariant();
      mirror(v);
      if (v && v.id !== lastVariantId) {
        lastVariantId = v.id;
        swapVariant(v);
      } else if (!silent) {
        try {
          var u2 = new URL(window.location.href);
          if (val === 'black') u2.searchParams.set('colour', 'black'); else u2.searchParams.delete('colour');
          window.history.replaceState(window.history.state, '', u2.toString());
        } catch (e) {}
      }
      if (!silent) track('tub_colour', { colour: val, product: C.product.handle });
      update();
    }
    $$('input[name$="-colour"]').forEach(function (r) {
      r.addEventListener('change', function () { if (r.checked) setColour(r.value); });
    });

    /* Refresh the theme's own price block (sale/VAT logic lives there) + delivery estimate. */
    var sectionCache = {};
    function swapVariant(v) {
      try {
        var u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        if (S.colour === 'black') u.searchParams.set('colour', 'black'); else u.searchParams.delete('colour');
        window.history.replaceState(window.history.state, '', u.toString());
      } catch (e) {}
      var url = C.product.url + '?variant=' + v.id + '&sections=' + encodeURIComponent(C.sectionId);
      var p = sectionCache[url] || (sectionCache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.json(); }));
      p.then(function (json) {
        var html = json && json[C.sectionId];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = document.getElementById('shopify-section-' + C.sectionId) || document;
        section.querySelectorAll('[data-dynamic-variant-content]').forEach(function (el) {
          if (root.contains(el)) return;
          var key = el.getAttribute('data-dynamic-variant-content');
          var fresh = doc.querySelector('[data-dynamic-variant-content="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]');
          if (fresh) el.innerHTML = fresh.innerHTML;
        });
      }).catch(function () { delete sectionCache[url]; });
      root.dispatchEvent(new CustomEvent('on:variant:change', {
        bubbles: true,
        detail: { form: null, variant: { id: v.id, title: v.title, price: v.price, available: v.available, option1: v.title, options: [v.title] } }
      }));
    }

    /* RAL / BS 4800 search (lazy-loaded list; RAL chips paint from the licensed sprite). */
    var COLOURS = null, coloursP = null;
    function loadColours() {
      if (coloursP) return coloursP;
      if (window.OmnSwatch && C.sprite) window.OmnSwatch.cfg(C.sprite, 46, 45);
      coloursP = fetch(C.colours).then(function (r) { return r.json(); }).then(function (d) {
        COLOURS = (d && d.colours) || [];
        renderRal();
        return COLOURS;
      }).catch(function () { coloursP = null; COLOURS = []; });
      return coloursP;
    }
    function colourLabel(c) {
      var code = c.system === 'BS4800' ? 'BS 4800 ' + c.code : c.code;
      var name = String(c.name || '').replace(/\b([a-z])/g, function (m, ch) { return ch.toUpperCase(); });
      return code + (name ? ' ' + name : '');
    }
    function paintChip(el, c) {
      if (!el || !c) return;
      if (c.hex) { el.style.background = c.hex; return; }
      if (window.OmnSwatch && c.si != null) window.OmnSwatch.applyTo(el, c.si);
    }
    function renderRal() {
      var list = $('[data-otb-ral-list]');
      var qEl = $('[data-otb-ral-q]');
      if (!list || !COLOURS) return;
      var q = (qEl && qEl.value || '').trim().toLowerCase();
      var hits;
      if (!q) {
        hits = [];
      } else {
        var qn = q.replace(/^ral\s*/, '').replace(/^bs\s*4800\s*/, '');
        hits = COLOURS.filter(function (c) {
          var hay = (c.code + ' ' + c.name + ' ' + (c.system === 'BS4800' ? 'bs 4800 ' + c.code : '')).toLowerCase();
          return hay.indexOf(q) > -1 || hay.replace(/\s+/g, '').indexOf(qn.replace(/\s+/g, '')) > -1;
        }).slice(0, 24);
      }
      list.innerHTML = '';
      if (q && !hits.length) {
        list.innerHTML = '<li class="otb-ral__none">No match. Type the reference in the box below and we\'ll match it.</li>';
        return;
      }
      hits.forEach(function (c) {
        var li = document.createElement('li');
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'otb-ral__opt';
        b.setAttribute('role', 'option');
        b.innerHTML = '<span class="otb-ral__chip"></span><span>' + esc(colourLabel(c)) + '</span>';
        paintChip(b.firstChild, c);
        b.addEventListener('click', function () { pickRal(c); });
        li.appendChild(b);
        list.appendChild(li);
      });
    }
    function pickRal(c) {
      S.ral = c;
      var picked = $('[data-otb-ral-picked]');
      if (picked) {
        picked.hidden = false;
        picked.innerHTML = '<span class="otb-ral__chip"></span><span>' + esc(colourLabel(c)) + '</span>' +
          '<button type="button" class="otb-link" data-otb-ral-clear>Change</button>';
        paintChip(picked.firstChild, c);
        picked.querySelector('[data-otb-ral-clear]').addEventListener('click', function () {
          S.ral = null; picked.hidden = true; picked.innerHTML = '';
          var qEl = $('[data-otb-ral-q]'); if (qEl) { qEl.focus(); }
          update();
        });
      }
      var list = $('[data-otb-ral-list]'); if (list) list.innerHTML = '';
      var qEl = $('[data-otb-ral-q]'); if (qEl) qEl.value = '';
      clearError();
      update();
    }
    var qInput = $('[data-otb-ral-q]');
    if (qInput) {
      var qT;
      qInput.addEventListener('input', function () { clearTimeout(qT); qT = setTimeout(renderRal, 120); });
      qInput.addEventListener('keydown', function (e) { if (e.key === 'Enter') e.preventDefault(); });
    }
    var refInput = $('[data-otb-ref]');
    if (refInput) refInput.addEventListener('input', function () { S.ref = refInput.value.trim(); clearError(); update(); });

    /* ?colour=<slug> deep links (ads): preselect Custom + that colour. */
    (function () {
      var slug = null;
      try { slug = new URL(window.location.href).searchParams.get('colour'); } catch (e) {}
      if (!slug) { try { slug = sessionStorage.getItem('omn_colour::' + C.product.handle); } catch (e) {} }
      if (!slug && C.startRef && C.custom) {
        /* Landed on a named colour variant (?variant=…): pick that colour under Custom. */
        var ref = String(C.startRef);
        setColour('custom', true);
        loadColours().then(function (list) {
          var t = ' ' + ref.toLowerCase().replace(/\s+/g, ' ') + ' ';
          var hit = (list || []).filter(function (c) {
            var code = String(c.system === 'BS4800' ? 'bs 4800 ' + c.code : c.code).toLowerCase();
            return t.indexOf(' ' + code + ' ') > -1 || t.indexOf(' ' + code.replace(/^ral /, '') + ' ') > -1;
          }).sort(function (a, b) { return String(b.code).length - String(a.code).length; })[0];
          if (hit) pickRal(hit);
          else if (refInput) { refInput.value = ref; S.ref = ref; update(); }
        });
        return;
      }
      if (!slug || !C.custom) return;
      slug = slugify(slug);
      if (slug === 'black') { setColour('black', true); return; }
      setColour('custom', true);
      loadColours().then(function (list) {
        var hit = (list || []).filter(function (c) {
          return slugify(c.code) === slug || slugify((c.system === 'BS4800' ? 'bs-4800-' : '') + c.code) === slug;
        })[0];
        if (hit) pickRal(hit);
        else if (refInput && /^ral-/.test(slug)) { refInput.value = slug.replace(/-/g, ' ').toUpperCase(); S.ref = refInput.value; update(); }
      });
    })();

    /* ---------- overflow ---------- */
    function wasteHasOverflow() { return !S.waste || !WASTES[S.waste] || WASTES[S.waste].overflow !== false; }
    function proj() { var w = WASTES[S.waste]; return (w && w.proj) || 48; }
    function sideState(k) { return R && R.ov ? R.ov[k] : null; }
    function sideLabel(k) {
      var pos = (k === 'A' || k === 'C') ? 'end' : 'long side';
      if (k === 'C' && R && R.cNote) pos = R.cNote;
      return 'Side ' + k + ' (' + pos + ')';
    }
    function paintSides() {
      $$('[data-otb-side]').forEach(function (b) {
        var k = b.getAttribute('data-otb-side');
        var st = sideState(k);
        var lbl = b.querySelector('[data-otb-side-st]');
        b.classList.remove('is-flush', 'is-proud', 'is-na', 'is-on');
        if (!st) {
          b.disabled = true; b.classList.add('is-na');
          if (lbl) lbl.textContent = (k === 'C' && R && R.cNote) ? R.cNote : 'n/a';
        } else {
          b.disabled = false; b.classList.add(st === 'flush' ? 'is-flush' : 'is-proud');
          if (lbl) lbl.textContent = st === 'flush' ? 'flush' : 'stands proud';
        }
        var on = (S.side === k);
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        b.setAttribute('aria-label', sideLabel(k) + (st ? (st === 'flush' ? ', sits flush' : ', stands proud about ' + proj() + ' mm') : ', not available'));
      });
      var mark = $('[data-otb-mark]');
      if (mark) {
        if (S.side && SIDE_POS[S.side]) {
          mark.hidden = false;
          mark.style.left = SIDE_POS[S.side][0] + '%';
          mark.style.top = SIDE_POS[S.side][1] + '%';
        } else { mark.hidden = true; }
      }
      var note = $('[data-otb-ov-note]');
      if (note) {
        if (!S.side) note.textContent = '';
        else if (sideState(S.side) === 'flush') note.textContent = 'On ' + sideLabel(S.side) + ' the overflow sits flush. Drilled at the ' + R.soak + ' mm soak line.';
        else note.textContent = 'On ' + sideLabel(S.side) + ' the overflow fitting stands out about ' + proj() + ' mm. Fine if the tub is recessed into a wall; otherwise allow clearance. Drilled at the ' + R.soak + ' mm soak line.';
      }
    }
    function setOv(on) {
      S.ov = !!on;
      var r = root.querySelector('input[name$="-ov"][value="' + (on ? 'yes' : 'no') + '"]');
      if (r) r.checked = true;
      var plan = $('[data-otb-plan]');
      if (plan) plan.hidden = !S.ov;
      if (!S.ov) S.side = null;
      else if (!S.side && R) {
        var flush = ['A', 'B', 'C', 'D'].filter(function (k) { return sideState(k) === 'flush'; });
        if (flush.length === 1) S.side = flush[0];
      }
      clearError();
      paintSides();
      update();
    }
    $$('input[name$="-ov"]').forEach(function (r) {
      r.addEventListener('change', function () { if (r.checked) { setOv(r.value === 'yes'); if (r.value === 'yes') track('tub_overflow', { product: C.product.handle }); } });
    });
    $$('[data-otb-side]').forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        S.side = b.getAttribute('data-otb-side');
        clearError(); paintSides(); update();
      });
    });
    var tip = $('[data-otb-tip]');
    if (tip) tip.addEventListener('click', function () {
      var body = document.getElementById(tip.getAttribute('aria-controls'));
      var open = tip.getAttribute('aria-expanded') !== 'true';
      tip.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (body) body.hidden = !open;
    });

    /* ---------- overflow information pop-up (snippets/omn-tub-overflow-info.liquid) ---------- */
    var ovDlg = $('[data-otb-ovinfo]');
    var ovOpener = null;
    function closeOvInfo() {
      if (!ovDlg) return;
      if (typeof ovDlg.close === 'function' && ovDlg.open) ovDlg.close();
      else ovDlg.removeAttribute('open');
    }
    if (ovDlg) {
      /* Native <dialog> sits in the top layer, so the buy box's own stacking can't clip it. Move it to
         <body> so a transformed ancestor (sticky product column) can't trap it in older browsers. */
      Array.prototype.forEach.call(document.querySelectorAll('body > [data-otb-ovinfo]'), function (d) {
        if (d.id === ovDlg.id) d.remove(); /* stale copy after a theme-editor section reload */
      });
      document.body.appendChild(ovDlg);
      $$('[data-otb-ovinfo-open]').forEach(function (b) {
        b.addEventListener('click', function () {
          ovOpener = b;
          if (typeof ovDlg.showModal === 'function') ovDlg.showModal();
          else ovDlg.setAttribute('open', '');
          document.documentElement.classList.add('otb-dlg-open');
          var yours = ovDlg.querySelector('.otb-dlg__sec.is-yours');
          var body = ovDlg.querySelector('.otb-dlg__body');
          if (body) body.scrollTop = 0;
          if (yours && body && yours.offsetTop - body.offsetTop > body.clientHeight * 0.5) {
            body.scrollTop = yours.offsetTop - body.offsetTop - 12;
          }
          var x = ovDlg.querySelector('.otb-dlg__close');
          if (x) x.focus();
          track('tub_overflow_info', { product: C.product.handle });
        });
      });
      ovDlg.querySelectorAll('[data-otb-ovinfo-close]').forEach(function (b) { b.addEventListener('click', closeOvInfo); });
      /* Tap on the dimmed backdrop closes it (the click lands on the <dialog> itself, outside the panel). */
      ovDlg.addEventListener('click', function (e) { if (e.target === ovDlg) closeOvInfo(); });
      ovDlg.addEventListener('close', function () {
        document.documentElement.classList.remove('otb-dlg-open');
        if (ovOpener) ovOpener.focus();
      });
    }

    /* ---------- taps ---------- */
    $$('input[name$="-taps"]').forEach(function (r) {
      r.addEventListener('change', function () { if (r.checked) { S.taps = (r.value === 'yes'); clearError(); update(); } });
    });

    /* ---------- insulation (thermal build) ---------- */
    var INS = (C.insul && window.OmnInsul) || null;
    var INS_R = null, INS_SURF = [];
    try { INS_R = INS ? INS.resolveProduct(C.product.handle) : null; INS_SURF = INS_R ? INS.surfacesFor(C.product.handle) : []; } catch (e) { INS_R = null; }
    var insStep = root.querySelector('[data-otb-step="insul"]');
    if (insStep && (!INS_R || INS_R.shell.q)) { insStep.hidden = true; INS_R = null; }   /* module and list disagree: hide, never misprice */
    var SURF_NAME = { base: 'Base', walls: 'Sides', seat: (INS_R && INS_R.shell.r === 'plunge') ? 'Steps' : 'Seat' };
    if (INS_R) {
      $$('[data-otb-insul-field]').forEach(function (f) {
        var k = f.getAttribute('data-otb-insul-field');
        f.hidden = INS_SURF.indexOf(k) < 0;
        var l = f.querySelector('[data-otb-insul-lbl]'); if (l) l.textContent = SURF_NAME[k];
      });
      var grid = $('[data-otb-insul-grid]'); if (grid) grid.classList.toggle('otb-insul__grid--3', INS_SURF.length > 2);
    }
    function insThickness() { var t = {}; INS_SURF.forEach(function (k) { t[k] = S.ins[k]; }); return t; }
    function insPrice() {
      if (!INS_R) return null;
      try { return INS.price(C.product.handle, insThickness(), { quantity: 1 }); } catch (e) { return null; }
    }
    /* What reaches the summary and the basket: switched on and priced with a real variant. */
    function insLive() { if (!S.insul) return null; var r = insPrice(); return (r && r.ok && r.variantId) ? r : null; }
    function insSpecWords(t) {
      var ks = Object.keys(t), v0 = t[ks[0]];
      if (ks.every(function (k) { return t[k] === v0; })) return v0 + ' mm throughout';
      return ks.map(function (k) { return SURF_NAME[k] + ' ' + (t[k] ? t[k] + ' mm' : 'none'); }).join(' · ');
    }
    function renderInsul() {
      if (!INS_R) return;
      var panel = $('[data-otb-insul]'); if (panel) panel.hidden = !S.insul;
      var r = insPrice();
      var from = $('[data-otb-insul-from]');
      if (from) {
        if (r && r.ok) { from.textContent = '+' + money(r.price * 100); from.className = 'otb-step__price'; }
        else { from.textContent = 'optional'; from.className = 'otb-step__opt'; }
      }
      var out = $('[data-otb-insul-out]');
      if (!out) return;
      if (!S.insul) { out.innerHTML = ''; return; }
      if (!r || !r.ok) {
        out.innerHTML = '<p class="otb-help">' + esc(r && r.reason === 'none'
          ? 'Choose a thickness for at least one surface, or choose No thanks.'
          : 'We can\'t price this build online. Call us on ' + (C.phone || '01934 751200') + ' and we\'ll quote it.') + '</p>';
        return;
      }
      var o = r.outer, a = o.after, g = o.grow, size;
      if (g.L || g.W) {
        var bits = [];
        if (g.L) bits.push(g.L + ' mm to the length');
        if (g.W) bits.push(g.W + ' mm to the width');
        size = 'The board comes out past the flange, adding ' + bits.join(' and ') + '.';
      } else {
        size = 'The board sits inside the flange, so the length and width don\'t change.';
      }
      if (g.H) size += ' The base adds ' + g.H + ' mm to the height.';
      var min = (r.breakdown && r.breakdown.preRound < INS.MIN_PRICE && r.price === INS.MIN_PRICE) ? '<p class="otb-help otb-help--sm">Minimum insulation charge ' + money(INS.MIN_PRICE * 100) + '.</p>' : '';
      out.innerHTML =
        '<div class="otb-insul__price"><span>Insulation · ' + esc(insSpecWords(r.thickness)) + '</span><strong>' + money(r.price * 100) + '</strong></div>' +
        '<p class="otb-insul__size"><span>Outside size once insulated</span><strong>' + esc(INS.outerLabel(o)) + '</strong></p>' +
        '<p class="otb-help otb-help--sm">' + esc(size) + ' Allow for this in your space and doorways.</p>' +
        '<p class="otb-help otb-help--sm">Adds about ' + INS.LEAD_TIME_EXTRA_WEEKS + ' weeks to the lead time.</p>' + min;
    }
    $$('input[name$="-insul"]').forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        S.insul = (r.value === 'yes');
        clearError(); renderInsul(); update();
        if (S.insul) track('tub_insulation', { product: C.product.handle });
      });
    });
    var insOn = root.querySelector('input[name$="-insul"]:checked');
    if (insOn && INS_R) S.insul = (insOn.value === 'yes');   /* back/forward cache keeps the choice */
    $$('[data-otb-insul-sel]').forEach(function (sel) {
      var k = sel.getAttribute('data-otb-insul-sel');
      S.ins[k] = Number(sel.value);
      sel.addEventListener('change', function () { S.ins[k] = Number(sel.value); clearError(); renderInsul(); update(); });
    });

    /* ---------- finish: polished / matt ---------- */
    function mattOn() { return !!(C.matt && S.matt); }
    function renderMatt() {
      if (!C.matt) return;
      var v = tubVariant();
      var el = $('[data-otb-matt-price]'); if (el) el.textContent = '+' + money(mattFee(v ? v.price : 0));
      var more = $('[data-otb-matt-more]'); if (more) more.hidden = !S.matt;
    }
    $$('input[name$="-matt"]').forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        S.matt = (r.value === 'yes');
        clearError(); renderMatt(); update();
        track('tub_finish', { finish: S.matt ? 'matt' : 'polished', product: C.product.handle });
      });
    });
    var mattChecked = root.querySelector('input[name$="-matt"]:checked');
    if (mattChecked && C.matt) S.matt = (mattChecked.value === 'yes');   /* back/forward cache keeps the choice */

    /* ---------- waste ---------- */
    /* A waste with no stock finish in stock can't be bought here: hide it rather than offer a dead choice. */
    $$('input[name$="-waste"]').forEach(function (r) {
      if (!r.value) return;
      var w = WASTES[r.value];
      if (!w || !w.finishes || !w.finishes.length) {
        var lab = r.closest('label'); if (lab) lab.hidden = true;
        r.disabled = true;
      }
    });
    function renderFinishes() {
      var box = $('[data-otb-finishes]');
      var sw = $('[data-otb-swatches]');
      var nm = $('[data-otb-finish-name]');
      var w = WASTES[S.waste];
      if (!box || !sw) return;
      if (!w) { box.hidden = true; sw.innerHTML = ''; return; }
      box.hidden = false;
      sw.innerHTML = '';
      w.finishes.forEach(function (f) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'otb-swatch';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', S.finish && S.finish.id === f.id ? 'true' : 'false');
        b.setAttribute('aria-label', f.name + ', ' + money(f.price));
        b.title = f.name;
        var dot = document.createElement('span');
        dot.className = 'otb-swatch__dot';
        dot.style.background = FIN_HEX[String(f.name).toLowerCase()] || '#ddd';
        b.appendChild(dot);
        b.addEventListener('click', function () { S.finish = f; renderFinishes(); clearError(); update(); });
        sw.appendChild(b);
      });
      if (nm) nm.textContent = S.finish ? (S.finish.name + ' · ' + money(S.finish.price)) : 'choose one';
    }
    $$('input[name$="-waste"]').forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        S.waste = r.value;
        var w = WASTES[S.waste];
        S.finish = (w && w.finishes && w.finishes[0]) || null;
        renderFinishes();
        var off = $('[data-otb-ov-off]');
        var ovStep = root.querySelector('[data-otb-step="overflow"]');
        if (!wasteHasOverflow()) {
          if (S.ov) setOv(false);
          if (off) off.hidden = false;
          if (ovStep) ovStep.classList.add('is-off');
          $$('input[name$="-ov"]').forEach(function (i) { i.disabled = true; });
        } else {
          if (off) off.hidden = true;
          if (ovStep) ovStep.classList.remove('is-off');
          $$('input[name$="-ov"]').forEach(function (i) { i.disabled = false; });
        }
        paintSides();
        clearError();
        update();
        if (S.waste) track('tub_waste', { waste: S.waste, product: C.product.handle });
      });
    });

    /* ---------- notes ---------- */
    var notesEl = $('[data-otb-notes]');
    if (notesEl) notesEl.addEventListener('input', function () { S.notes = notesEl.value; S.notesDirty = true; });

    /* ---------- summary ---------- */
    function lines() {
      var out = [];
      var v = tubVariant();
      var cName = S.colour === 'white' ? 'White' : S.colour === 'black' ? 'Black' :
        (S.ral ? colourLabel(S.ral) : (S.ref ? S.ref : 'Custom colour'));
      out.push({ label: 'Tub · ' + cName, price: v ? v.price : 0 });
      if (mattOn()) out.push({ label: 'Matt finish', price: mattFee(v ? v.price : 0) });
      if (S.ov && R) out.push({ label: 'Overflow drilling' + (S.side ? ' · Side ' + S.side : ''), price: C.services.overflow.price });
      if (S.taps && R && R.taps) out.push({ label: 'Tap hole drilling', price: C.services.taps.price });
      if (S.waste && WASTES[S.waste] && S.finish) out.push({ label: WASTES[S.waste].title + ' · ' + S.finish.name, price: S.finish.price });
      var ir = insLive();
      if (ir) out.push({ label: 'Insulation · ' + insSpecWords(ir.thickness), price: ir.price * 100 });
      return out;
    }
    function update() {
      var ls = lines();
      var total = ls.reduce(function (a, l) { return a + (l.price || 0); }, 0);
      var ul = $('[data-otb-lines]');
      if (ul) ul.innerHTML = ls.map(function (l) { return '<li><span>' + esc(l.label) + '</span><span>' + money(l.price) + '</span></li>'; }).join('');
      var t = $('[data-otb-total]'); if (t) t.textContent = money(total);
      var st = $('[data-otb-sticky-total]'); if (st) st.textContent = money(total);
      var terms = $('[data-otb-drill-terms]'); if (terms) terms.hidden = !(S.ov || S.taps || insLive() || mattOn());
      var tw = $('[data-otb-terms-what]');
      if (tw) {
        var what = [];
        if (S.ov || S.taps) what.push('drilled');
        if (insLive()) what.push('insulated');
        if (mattOn()) what.push('finished in matt');
        tw.textContent = 'Once ' + (what.length > 1 ? what.slice(0, -1).join(', ') + ' or ' + what[what.length - 1] : (what[0] || 'drilled or insulated'));
      }
      renderMatt();
      var v = tubVariant();
      var ok = !!(v && v.available);
      var add = $('[data-otb-add]');
      if (add) {
        add.disabled = !ok;
        add.textContent = ok ? 'Add to basket' : 'Currently unavailable';
      }
      var sAdd = $('[data-otb-sticky-add]');
      if (sAdd) {
        sAdd.disabled = !ok;
        sAdd.textContent = ok ? 'Add to basket' : 'Unavailable';
      }
    }

    /* ---------- validation + add ---------- */
    function showError(msg, focusEl) {
      var e = $('[data-otb-error]');
      if (e) { e.textContent = msg; e.hidden = false; }
      if (focusEl && focusEl.scrollIntoView) {
        focusEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (focusEl.focus) setTimeout(function () { try { focusEl.focus({ preventScroll: true }); } catch (x) {} }, 300);
      }
    }
    function clearError() { var e = $('[data-otb-error]'); if (e) { e.hidden = true; e.textContent = ''; } }
    function validate() {
      if (S.colour === 'custom' && !S.ral && !S.ref) {
        showError('Choose a RAL or BS 4800 colour, or type the paint reference you want us to match.', $('[data-otb-ral-q]'));
        return false;
      }
      if (S.ov && R && !S.side) {
        showError('Choose the side for the overflow on the tub plan.', $('[data-otb-plan]'));
        return false;
      }
      if (S.insul && INS_R && !insLive()) {
        var ir0 = insPrice();
        showError(ir0 && ir0.reason === 'none'
          ? 'Choose a thickness for at least one surface, or choose No thanks for insulation.'
          : 'We can\'t add this insulation online. Choose No thanks and call us on ' + (C.phone || '01934 751200') + ' to add it.', $('[data-otb-insul]'));
        return false;
      }
      if (S.waste && !S.finish) {
        showError('Choose a finish for your waste.', $('[data-otb-finishes]'));
        return false;
      }
      return true;
    }

    function sendEvidence(payload) {
      var url = C.evidence && C.evidence.url;
      if (!url) return Promise.resolve();
      var base = url.replace(/\/sign\/?$/, '');
      var body;
      try { body = JSON.stringify(payload); } catch (e) { return Promise.resolve(); }
      var go = fetch(base + '/nonce', { cache: 'no-store' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; })
        .then(function (j) {
          var h = { 'Content-Type': 'application/json' };
          if (j && j.nonce) h['X-Omn-Nonce'] = j.nonce;
          return fetch(url, { method: 'POST', headers: h, body: body }).catch(function () {});
        });
      return Promise.race([go, new Promise(function (res) { setTimeout(res, 4000); })]);
    }

    function saveNotes() {
      /* Only when typed here: an untouched box must not wipe notes written in the basket. */
      if (!S.notesDirty) return Promise.resolve();
      var val = (S.notes || '').trim().slice(0, 500);
      return fetch((window.theme && theme.routes && theme.routes.cartUpdate || '/cart/update') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Customer notes': val } })
      }).then(function (r) { if (r.ok) S.notesDirty = false; }).catch(function () {});
    }

    var busy = false;
    function add() {
      if (busy) return;
      clearError();
      if (!validate()) return;
      var v = tubVariant();
      if (!v || !v.available) return;
      busy = true;
      var btns = $$('[data-otb-add],[data-otb-sticky-add]');
      btns.forEach(function (b) { b.classList.add('is-loading'); b.setAttribute('aria-disabled', 'true'); });

      var build = 'TUB-' + uid();
      var drilled = (S.ov && R && S.side) || (S.taps && R && R.taps);
      var token = drilled ? 'OMNDRILL-' + uid() : '';
      var terms = (C.evidence && C.evidence.terms) || '';

      var tubProps = { _omn_build: build, _omn_host: '1' };
      if (S.colour === 'black') { tubProps['Requested colour'] = 'Black'; }
      if (S.colour === 'custom') {
        if (S.ral) { tubProps['Requested colour'] = colourLabel(S.ral); tubProps._colour_code = S.ral.code; }
        if (S.ref) tubProps['Paint reference'] = S.ref;
      }
      var insR = insLive();
      var matt = mattOn();
      /* Matt: "Finish" is the property the panel-price Cart Transform reads to add the 25% fee and the
         made-to-order checks read to require the sign-off. The figure itself is never trusted. */
      if (matt) {
        tubProps['Finish'] = 'Matt';
        tubProps._omn_matte_price = String(Math.round(mattFee(v.price) / 100));
        tubProps['Lead time'] = 'Matt finish adds about 5 working days';
      }
      var made = [];
      if (drilled) made.push('drilled');
      if (insR) made.push('insulated');
      if (matt) made.push('finished in matt');
      if (made.length) {
        tubProps['Bespoke — cut to order'] = 'Non-returnable once ' + (made.length > 1 ? made.slice(0, -1).join(', ') + ' and ' + made[made.length - 1] : made[0]) +
          '. You’ll sign the made-to-order agreement in your basket' + (drilled ? ', and your specification is emailed with your order.' : '.');
      }
      /* Insulated crate size rides on the tub line: Shopify leaves no-shipping lines out of rate requests. */
      if (insR) { try { Object.assign(tubProps, INS.tubLineProperties(insR)); } catch (e) {} }

      var addons = [];
      var ovSpec = null, tapSpec = null;
      if (S.ov && R && S.side) {
        var flush = sideState(S.side) === 'flush';
        ovSpec = { side: S.side, label: sideLabel(S.side), flush: flush, proj: flush ? 0 : proj(), soak: R.soak };
        addons.push({ id: C.services.overflow.id, quantity: 1, properties: {
          'Service': 'Overflow hole — drilled to order',
          'Position': sideLabel(S.side),
          'Bore': 'Ø52 mm',
          'Fit': flush ? 'Flush' : 'Stands proud ~' + proj() + ' mm',
          _omn_service: 'overflow', _omn_drill_terms: terms, _omn_drill_evidence: token,
          _omn_build: build, _omn_addon: '1'
        } });
      }
      if (S.taps && R && R.taps) {
        tapSpec = { side: 'A', label: 'Side A (end)' };
        addons.push({ id: C.services.taps.id, quantity: 1, properties: {
          'Service': 'Tap holes — drilled to order',
          'Layout': 'Standard ¾″ tails at 180 mm centres',
          'Position': 'Side A (end)',
          _omn_service: 'taps', _omn_drill_terms: terms, _omn_drill_evidence: token,
          _omn_build: build, _omn_addon: '1'
        } });
      }
      if (insR) {
        var insProps = INS.cartLineProperties(insR, { build: build });
        insProps['Insulation'] = insSpecWords(insR.thickness);   /* same words as the picker (Sides, not Walls); _omn_insul_spec keeps the data */
        insProps._omn_addon = '1';
        addons.push({ id: Number(insR.variantId), quantity: 1, properties: insProps });
      }
      if (S.waste && S.finish) {
        addons.push({ id: S.finish.id, quantity: 1, properties: {
          _omn_waste_for: C.product.handle, _omn_build: build, _omn_addon: '1'
        } });
      }
      /* /cart/add.js lists a multi-item add newest-first, so send add-ons first and the tub
         last; cart-items.liquid also regroups add-ons under their tub by _omn_build. */
      var items = addons.slice().reverse().concat([{ id: v.id, quantity: 1, properties: tubProps }]);

      var drawer = document.querySelector('cart-drawer');
      var sections = ['cart-icon-bubble'];
      var drawerSection = drawer && drawer.closest('.shopify-section') ? drawer.closest('.shopify-section').id.replace('shopify-section-', '') : null;
      if (drawerSection) sections.push(drawerSection);

      var evidence = drilled ? sendEvidence({
        token: token, signedAt: '', terms: terms, name: '', range: C.range, signAtCheckout: true,
        config: { tub: C.product.title, colour: tubProps['Requested colour'] || (S.colour === 'white' ? 'White' : ''), paintRef: S.ref || '',
          waste: S.waste && WASTES[S.waste] ? WASTES[S.waste].title : '', finish: S.finish ? S.finish.name : '', source: 'pdp' },
        overflow: ovSpec, taps: tapSpec, trim: null, planImage: C.plan || '', signature: ''
      }) : Promise.resolve();

      var cartAdd = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
      evidence
        .then(saveNotes)
        .then(function () {
          return fetch(cartAdd + (/\.js$/.test(cartAdd) ? '' : '.js'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: items, sections: sections.join(','), sections_url: window.location.pathname })
          });
        })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); })
        .then(function (res) {
          if (!res.ok) {
            var msg = (res.json && (res.json.description || res.json.message)) || 'Sorry, we couldn\'t add that to your basket. Please try again, or call us.';
            throw new Error(typeof msg === 'string' ? msg : 'Sorry, something went wrong.');
          }
          var data = res.json || {};
          track('tub_add_to_cart', { product: C.product.handle, colour: S.colour, overflow: !!ovSpec, taps: !!tapSpec, insulation: insR ? insR.price : 0, matt: matt, waste: S.waste || '' });
          try {
            if (data.sections && data.sections['cart-icon-bubble']) {
              var bubble = document.getElementById('cart-icon-bubble');
              if (bubble) bubble.innerHTML = data.sections['cart-icon-bubble'];
            }
          } catch (e) {}
          var afterAtc = window.theme && theme.settings && theme.settings.afterAtc;
          if (afterAtc === 'page' || !drawer) { window.location.href = (window.theme && theme.routes && theme.routes.cart) || '/cart'; return; }
          if (data.sections && drawer.renderContents) drawer.renderContents(data, true);
          else if (drawer.refresh) drawer.refresh(true);
          return fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { cart: cart, variantId: v.id } }));
            }).catch(function () {});
        })
        .catch(function (err) {
          showError((err && err.message) || 'Sorry, something went wrong. Please try again.');
          track('tub_add_error', { product: C.product.handle, error: String(err && err.message || '') });
        })
        .then(function () {
          busy = false;
          btns.forEach(function (b) { b.classList.remove('is-loading'); b.removeAttribute('aria-disabled'); });
        });
    }
    var addBtn = $('[data-otb-add]');
    if (addBtn) addBtn.addEventListener('click', add);
    var stickyAdd = $('[data-otb-sticky-add]');
    if (stickyAdd) stickyAdd.addEventListener('click', add);

    /* ---------- phone sticky bar ---------- */
    var sticky = $('[data-otb-sticky]');
    var summary = $('[data-otb-summary]');
    if (sticky && summary && 'IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          var show = !en.isIntersecting;
          sticky.classList.toggle('is-shown', show);
          sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
          if (stickyAdd) stickyAdd.tabIndex = show ? 0 : -1;
        });
      }, { threshold: 0 });
      io.observe(summary);
    }

    paintSides();
    renderFinishes();
    renderInsul();
    setColour(S.colour, true);
    update();
  }

  function boot() { document.querySelectorAll('[data-omn-tub-buybox]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
